import { usePeriod } from '../../payload/PeriodContext'
import {
  Card,
  CardHead,
  Pipeline,
  SectionHead,
  Tile,
  Tiles,
} from '../components'
import { num, s } from '../format'
import { useBlank } from './useBlank'

/**
 * The at-a-glance section: who the users are, how they arrive, what they do
 * once they are in. Everything here moved from elsewhere rather than being
 * copied — two renderings of one dataset is how they come to disagree.
 */
const Highlights = () => {
  const { one, panel, windowSuffix } = usePeriod()
  const blank = useBlank()
  const o = one('overview')
  const acq = panel('acquisition')
  const appSteps = panel('app_pipeline')
  const unlinkable = acq.filter(step => step.cannot_be_linked)

  return (
    <section className="grid gap-4">
      <SectionHead
        title="Highlights"
        question="Where the product stands, in three rows: who the users are, how they arrive, and what they do once they are in."
      />

      <Tiles>
        <Tile
          hero
          label={`Real user accounts${windowSuffix}`}
          value={num(o.real_users)}
        />
        {/* Both count people, not activity — the labels say so (Copilot review
            of #944; the prototype read "Submitted surveys" / "3+ Saved answers"). */}
        <Tile label="Users who saved data" value={num(o.real_with_any_data)} />
        <Tile
          label="Users with 3+ answers"
          value={num(o.real_with_three_answers)}
        />
        <Tile label="Total answers" value={num(o.real_submissions)} />
        <Tile
          label="Total user accounts"
          value={num(o.registered_accounts_ever)}
        />
      </Tiles>

      <Card>
        <CardHead
          title="From zero to Hero"
          info={[
            'Every visitor who reached each step, with the conversion into that step shown on the step itself.',
            'This is NOT a funnel. Visitors who sign up on the website never install the app, so they enter partway along and a later step can be larger than an earlier one — a rate over 100% is flagged, not clipped.',
            'Counted as a cohort: visitors whose first visit falls in the selected period, and how far they have got since.',
            unlinkable.length >= 2 &&
              `The dashed edge and "no link" are not a rendering fault. Going via the App Store loses track of who the person was, so "${s(unlinkable[0].step)}" and "${s(unlinkable[1].step)}" can never be joined — both counts are real, the link between them is not, and it needs a deep link that survives the App Store to ever work.`,
          ]}
        />
        <p className="text-muted text-sm">
          Website visitors on zealth.io, from first visit to a finished profile.
          Anonymous and real visitors only: every account we can prove is ours
          is excluded at every step. Each step is an action someone took,
          counted from website and app activity.
        </p>
        {acq.length ? (
          <Pipeline rows={acq} labelKey="step" valueKey="visitors" />
        ) : (
          blank('acquisition', 'Nobody started the journey in this period.')
        )}
      </Card>

      <Card>
        <CardHead
          title="Hero path"
          info={[
            'The behaviour pipeline, real users only. The one above ends at signup; this one starts there and ends at the projection they came for.',
            'These steps are different kinds of act rather than a nested funnel — opening a survey, saving an answer, returning another day, looking at a chart — so a later step can exceed an earlier one and a rate over 100% is flagged rather than clipped.',
            'The last two steps are the point: everything before them is data entry, and the projection and the what-if are what the entry is for. A pipeline that stopped at "saved an answer" could not show whether anyone got the thing they came for.',
            'Sources are mixed deliberately — setting up, saving and returning come from the app database, where a saved answer is a record rather than an event; opening a survey and viewing a projection can only come from activity, because the database has no record of looking at something.',
            'Three in-app events were only shipped in the mid-August instrumentation change and are deliberately not steps here: on an all-time window they would read as a 90% drop that is instrumentation age, not behaviour.',
          ]}
        />
        <p className="text-muted text-sm">
          What real users do once they are in the app, from a saved profile to a
          projection. Real user accounts only. The first and fourth steps are
          records in the app database, the rest are actions — so this does not
          continue the strip above step for step.
        </p>
        {appSteps.length ? (
          <Pipeline rows={appSteps} labelKey="step" valueKey="users" />
        ) : (
          blank('app_pipeline', 'No real users were active in this period.')
        )}
      </Card>
    </section>
  )
}

export default Highlights
